import React, { useEffect, useState } from "react";
import { FileEdit, Save, Trash2, UploadCloud } from "lucide-react";
import Dropzone from "react-dropzone";
import { useFormik } from "formik";
import { imageInitialValues, imageValidationSchema } from "./validationSchema";
import { SubSection, useFormContext } from "context/BlogContext";
import DeleteModal from "Common/DeleteModal";
import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { generateUniqueId } from "utils/uuid";

interface ImageProps {
  sectionOrder: number;
  subSectionOrder: number;
  handleSaveSubSection: (
    sectionOrder: number,
    subSectionInfo: SubSection
  ) => void;
  handleEditSubSection: (sectionOrder: number, subSection: SubSection) => void;
  subSection: SubSection;
  handleDeleteSubSection: (subsectionOrder: number) => void;
}

const Images: React.FC<ImageProps> = ({
  sectionOrder,
  subSectionOrder,
  handleSaveSubSection,
  handleEditSubSection,
  subSection,
  handleDeleteSubSection,
}) => {
  const [selectedImage, setSelectedImage] = useState<any>([]);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [isCancel, setIsCancel] = useState(false);

  const { setBlogMedia, setDeletedMedia, blogMedia } = useFormContext();

  const selectDataList = createSelector(
    (state: any) => state.Blog,
    (state) => ({
      isLoading: state.isLoading,
    })
  );

  const { isLoading } = useSelector(selectDataList);

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: imageInitialValues(subSection),
    validationSchema: imageValidationSchema,
    onSubmit: (values) => {
      handleSaveSubSection(sectionOrder, {
        mediaId: values.imageId,
        title: values.title,
        type: "image",
        attrs: { src: values.src, alt: values.alt, caption: values.caption },
        isEditing: false,
        order: subSectionOrder,
      });
    },
  });

  const isBlobUrl = (url: string) => {
    return url.startsWith("blob:");
  };

  const handleAcceptImage = (files: any) => {
    const file = files[0]; // Only take the first file
    if (file) {
      const previewUrl = URL.createObjectURL(file);

      const isBlob = isBlobUrl(validation.values.src);
      if (!isBlob) {
        setDeletedMedia([validation.values.src]);
      }
      const imageId = generateUniqueId();
      const enhancedFile = Object.assign(file, {
        // id: imageId,
        preview: previewUrl,
        file: new Blob([file], { type: file.type }),
        blobName: file.name,
      });
      const key = `images${sectionOrder}ss${subSectionOrder}`;
      setBlogMedia((prev: any) => ({
        ...prev,
        [imageId]: enhancedFile, // Store the updated file object inside coverImage
      }));  
      setSelectedImage({ preview: previewUrl, name: file.name });

      validation.setFieldValue("src", previewUrl);
      validation.setFieldValue("imageId", imageId); // Save imageId
      if (!validation.values.alt) {
        validation.setFieldValue("alt", file.name); // Set default alt text to image name
      }
      // setSelectedImage(previewFile); // Store only one file
    }
  };

  // Handle delete confirmation
  const handleDelete = () => {
    handleDeleteSubSection(subSection.order);
    setDeleteModal(false);
  };

  const handleCancelSubSection = () => {
    handleSaveSubSection(sectionOrder, {
      ...subSection,
      isEditing: true,
      // isEditing: subSection.isEditing ? false : true,
    });
    setDeletedMedia([]);
    validation.resetForm();
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  return (
    <div>
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
        message="Are you sure you want to remove this subsection?"
      />
      {subSection.isEditing ? (
        <form>
          <div className="xl:col-span-6 pt-5">
            <label
              htmlFor={`subTitle`}
              className="inline-block mb-2 text-base font-medium"
            >
              Title
            </label>
            <input
              type="text"
              id="title"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Title"
              name="title"
              onChange={validation.handleChange} // Bind Formik onChange
              value={validation.values.title} // Bind Formik value
            />
          </div>
          <div className="pt-5">
            <label
              htmlFor="image"
              className="inline-block mb-2 text-base font-medium"
            >
              Upload Image
            </label>
            <Dropzone
              accept={{
                "image/jpeg": [".jpg", ".jpeg"],
                "image/png": [".png"],
                "image/webp": [".webp"],
              }}
              onDrop={(acceptedFiles: any) => {
                handleAcceptImage(acceptedFiles);
              }}
            >
              {({ getRootProps, getInputProps }: any) => (
                <div
                  {...getRootProps()} // Makes the preview clickable and enables drag-and-drop
                  className="flex w-full items-center justify-center bg-white border border-dashed rounded-md cursor-pointer dropzone border-slate-300 dropzone2 dark:bg-zink-700 dark:border-zink-500"
                >
                  <input {...getInputProps()} /> {/* File input field */}
                  {validation.values.src ? (
                    <div>
                      <div className="rounded border-slate-200 dark:border-zink-500">
                        <div className="p-2 text-center">
                          <div>
                            <div className="p-2 mx-auto rounded-md h-[200px] bg-slate-100 dark:bg-zink-600">
                              <img
                                data-dz-thumbnail
                                className="block w-full h-full rounded-md"
                                src={validation.values.src}
                                alt={selectedImage.name}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full py-5 text-lg text-center dz-message needsclick">
                      <div className="mb-3">
                        <UploadCloud className="block size-12 mx-auto text-slate-500 fill-slate-200 dark:text-zink-200 dark:fill-zink-500"></UploadCloud>
                      </div>
                      <h5 className="mb-0 font-normal text-slate-500 text-15">
                        Drag and drop your files or <a href="#!">browse</a> your
                        files
                      </h5>
                    </div>
                  )}
                </div>
              )}
            </Dropzone>
          </div>
          {validation.errors.src && validation.touched.src && (
            <p className="mt-1 text-sm text-red-400">
              {String(validation.errors.src)}
            </p>
          )}
          <div className="pt-5 grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-2">
            <div className="xl:cols-span-6">
              <label
                htmlFor="alt"
                className="inline-block mb-2 text-base font-medium"
              >
                Alt Text
              </label>
              <input
                type="text"
                id="alt"
                name="alt"
                placeholder="Alt"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                value={validation.values.alt}
                onChange={validation.handleChange}
              />
              {validation.errors.alt && validation.touched.alt && (
                <p className="mt-1 text-sm text-red-400">
                  {String(validation.errors.alt)}
                </p>
              )}
            </div>
            <div className="xl:cols-span-6">
              <label
                htmlFor="caption"
                className="inline-block mb-2 text-base font-medium"
              >
                Caption
              </label>
              <input
                type="text"
                id="caption"
                name="caption"
                placeholder="Caption"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                value={validation.values.caption}
                onChange={validation.handleChange}
              />
              {validation.errors.caption && validation.touched.caption && (
                <p className="mt-1 text-sm text-red-400">
                  {String(validation.errors.caption)}
                </p>
              )}
            </div>
          </div>
        </form>
      ) : (
        // If not editing, show the preview
        <div className="w-full">
          {validation.values.src && (
            <div className="flex justify-center">
              <img
                className="rounded-md"
                src={validation.values.src}
              />
            </div>
          )}
        </div>
      )}
      <div className="flex justify-end gap-x-2.5 pt-5">
        {subSection.isEditing ? (
          <div className="flex gap-x-3">
            {isCancel && (
              <button
                type="button"
                className={`${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                } text-red-500`}
                onClick={() => handleCancelSubSection()}
                disabled={isLoading}
              >
                <div className="flex align-center">
                  <span className="ml-1">Cancel</span>
                </div>
              </button>
            )}
            <button
              type="button"
              className={`text-green-500 ${
                isLoading
                  ? "cursor-not-allowed opacity-50"
                  : "hover:text-white hover:bg-green-600"
              } bg-green-100 btn focus:text-white focus:bg-green-600 focus:ring focus:ring-green-100 active:text-white active:bg-green-600 active:ring active:ring-green-100 dark:bg-green-500/20 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white dark:focus:bg-green-500 dark:focus:text-white dark:active:bg-green-500 dark:active:text-white dark:ring-green-400/20`}
              onClick={(e) => handleSubmit(e)}
              disabled={isLoading}
            >
              <div className="flex align-center">
                <Save className="size-4"></Save>
                <span className="ml-1">Save</span>
              </div>
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className={`text-custom-500 ${
                isLoading
                  ? "cursor-not-allowed opacity-50"
                  : "hover:text-white hover:bg-custom-600"
              } btn bg-custom-100 focus:text-white focus:bg-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:ring active:ring-custom-100 dark:bg-custom-500/20 dark:text-custom-500 dark:hover:bg-custom-500 dark:hover:text-white dark:focus:bg-custom-500 dark:focus:text-white dark:active:bg-custom-500 dark:active:text-white dark:ring-custom-400/20`}
              onClick={() => {
                handleEditSubSection(sectionOrder, subSection);
                setIsCancel(true);
              }}
              disabled={isLoading}
            >
              <div className="flex align-center">
                <FileEdit className="size-4"></FileEdit>
                <span className="ml-1">Edit</span>
              </div>
            </button>
          </>
        )}
        {/* {section?.body?.length > 1 && ( */}
        <button
          type="button"
          className={`${
            isLoading ? "cursor-not-allowed opacity-50" : ""
          } text-red-500`}
          onClick={() => setDeleteModal(true)}
          disabled={isLoading}
        >
          <div className="flex align-center">
            <Trash2 className="size-4 text-red-500"></Trash2>
            <span className="ml-1">Delete</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default Images;
